import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loading, Empty, ScreenBoundary } from "../components/states";
import {
  Button,
  CollapsibleSection,
  Footnote,
  Pill,
  SectionHeading,
  Sheet,
  plural,
  relativeDay,
} from "../components/kit";
import { useStable } from "../lib/useStable";
import { useViewer } from "../lib/viewer";
import { useBusyAction } from "../lib/useBusyAction";
import { useHeaderOffset } from "../lib/useHeaderOffset";
import { presentLevelsBasis } from "@shared/status";

/**
 * The carry list, and the floor and target on every item in it.
 *
 * One row of `clinicItems` holds both halves — *whether* this clinic carries
 * the thing, and the two numbers the weekly count fires against — because they
 * are one decision: "we keep gauze, and we keep at least four boxes of it" is a
 * sentence a clinic manager says once. So this is one screen, not a carry-list
 * screen plus a levels screen.
 *
 * **Draft honesty is the load-bearing part.** The seeded levels are our guess;
 * Deanna is bringing the real numbers. A guess presented as a decision is
 * exactly the thing that gets pointed at on a projector, so every unconfirmed
 * row wears the tag `presentLevelsBasis` gives it, and a note at the top of the
 * screen counts how many are left. The server flips a row to `confirmed` on any
 * human save — nothing here decides that, it only reports it.
 *
 * **One save mechanism, and it is a button.** Saving on blur was the other
 * option and it loses twice: on a phone "blur" is a tap anywhere, so a write
 * happens at a moment nobody chose; and editing floor before target means the
 * intermediate pair is briefly invalid, which would fire a server refusal —
 * "the target cannot be below the floor" — as a toast, for a state the person
 * was two seconds away from fixing. A Save button that appears when the row is
 * dirty sends **both numbers together**, checks the pair on the client first,
 * and shows the one failure a person can cause as a field-level message under
 * the inputs rather than as a toast.
 *
 * Who may do what is the server's call and is only *reflected* here:
 * `levels.list` returns `canEdit`, an assistant gets `false`, and this screen
 * draws no inputs, no add and no remove when it does. The warehouse is refused
 * outright — clinic stocking levels are the clinic's.
 */

/** Derived from the query's own return type, so a field added on the server
 *  arrives here with no edit and a field removed fails the build at the row
 *  that reads it. */
type Levels = FunctionReturnType<typeof api.levels.list>;
type Carried = Levels["carried"][number];

/** How many level rows are on screen before anything is tapped. Inventory's
 *  own rule at half the budget — see `groups` below for why. */
const ROWS_OPEN_ON_ARRIVAL = 8;

export function Levels() {
  const [attempt, setAttempt] = useState(0);
  return (
    <ScreenBoundary
      key={attempt}
      title="Stock levels could not load"
      heading={<SectionHeading title="Stock levels" />}
      onRetry={() => setAttempt((n) => n + 1)}
    >
      <LevelsScreen />
    </ScreenBoundary>
  );
}

function LevelsScreen() {
  const viewer = useViewer();
  /* An owner has no clinic of their own, and `levels.list` refuses rather than
     guessing which of the five was meant — so the picker is not decoration, it
     is the argument. Every other role is pinned server-side and never sees it. */
  const isOwner = viewer.role === "owner";
  const clinics = useQuery(api.clinics.list, isOwner ? {} : "skip");
  const [chosen, setChosen] = useState<Id<"clinics"> | null>(null);
  const clinicId = isOwner ? (chosen ?? clinics?.[0]?._id ?? null) : null;

  /* `useStable`: the picker drives this query's arguments, and without it
     every clinic change unmounts the whole screen into a skeleton. */
  const data = useStable(
    useQuery(api.levels.list, isOwner ? (clinicId ? { clinicId } : "skip") : {}),
  );

  const [addOpen, setAddOpen] = useState(false);
  const headerOffset = useHeaderOffset();

  /**
   * Carried rows come back sorted by subcategory then name, so grouping is a
   * single pass and the section order is the server's — the client's own sheet
   * sections (ANAESTHETICS, GLOVES, ENDO…).
   *
   * `openOnArrival` is the Inventory rule, with a smaller budget: a level row
   * is two inputs and a button line, roughly twice the height of an inventory
   * card, and seventy-eight of them arriving open is a 28,000px page. The top
   * of the list arrives open so there is something to work in immediately; the
   * tail arrives folded, each header already saying its count and how many of
   * its rows are still on draft levels.
   */
  const groups = useMemo(() => {
    const made: Array<{ name: string; rows: Carried[]; openOnArrival: boolean }> =
      [];
    for (const row of data?.carried ?? []) {
      const last = made[made.length - 1];
      if (last && last.name === row.subcategory) last.rows.push(row);
      else
        made.push({
          name: row.subcategory || "Uncategorised",
          rows: [row],
          openOnArrival: false,
        });
    }
    let shown = 0;
    for (const group of made) {
      group.openOnArrival = shown < ROWS_OPEN_ON_ARRIVAL;
      shown += group.rows.length;
    }
    return made;
  }, [data]);

  if (data === undefined) {
    return (
      <div data-screen-state="loading">
        <Loading rows={5} label="Loading stocking levels" />
      </div>
    );
  }

  const drafts = data.carried.filter(
    (row) => row.levelsBasis === "draft",
  ).length;

  return (
    <div data-screen-state="ready" className="space-y-4 pb-8">
      <SectionHeading
        title="Stock levels"
        subtitle={`${data.clinicName} · ${data.carried.length} ${plural(
          data.carried.length,
          "item",
        )} counted each week`}
        action={
          data.canEdit ? (
            <Button onClick={() => setAddOpen(true)}>Add an item</Button>
          ) : undefined
        }
      />

      {/* The registry `Select`, the same control `PeriodPicker` is. Full width
          at 390px: a clinic named "W68" and one named "Coquitlam Family
          Dental" must not be two different widths. */}
      {isOwner ? (
        <Select
          value={clinicId ?? undefined}
          onValueChange={(value) => setChosen(value as Id<"clinics">)}
        >
          <SelectTrigger aria-label="Clinic" className="w-full sm:w-72">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(clinics ?? []).map((clinic) => (
              <SelectItem key={clinic._id} value={clinic._id}>
                {clinic.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}

      {/* The honesty note, at the size a caveat deserves — the `ModelledTag`
          pattern, one screen over. It disappears on its own as rows are
          confirmed, which is the only progress bar this needs. */}
      {drafts > 0 ? (
        <Footnote tone="caution">
          <span className="font-semibold text-foreground">
            {drafts} {plural(drafts, "item")} still on draft levels.
          </span>{" "}
          These floors and targets are our estimate from the client's own order
          history, not this clinic's decision. Saving a row confirms it.
        </Footnote>
      ) : null}

      {!data.canEdit ? (
        <Footnote>
          Read-only. Your clinic manager or the owner sets these levels; the
          weekly count fires against them.
        </Footnote>
      ) : (
        /* The one thing the person setting these two numbers has to know, said
           on the screen that sets them — the mechanism lived only on the
           assistant's read-only note and nowhere the editor would see it. */
        <Footnote>
          A count below an item's <b className="font-semibold text-foreground">floor</b>{" "}
          automatically orders enough to reach its{" "}
          <b className="font-semibold text-foreground">target</b> — for the
          manager to approve before it ships.
        </Footnote>
      )}

      {data.carried.length === 0 ? (
        <Empty
          title="Nothing on the carry list yet"
          body="Add the items this clinic counts each week. Each one gets a floor it must not drop below and a target it is restocked to."
        />
      ) : (
        <div className="space-y-3 lg:grid lg:grid-cols-2 lg:items-start lg:gap-3 lg:space-y-0">
          {groups.map((group) => {
            const groupDrafts = group.rows.filter(
              (row) => row.levelsBasis === "draft",
            ).length;
            return (
              <CollapsibleSection
                key={group.name}
                title={group.name}
                count={group.rows.length}
                tone={groupDrafts > 0 ? "amber" : "gray"}
                defaultOpen={group.openOnArrival}
                stickyTop={headerOffset}
                summary={
                  groupDrafts > 0
                    ? `${groupDrafts} on draft levels`
                    : "all confirmed"
                }
              >
                {() => (
                  <div className="divide-y">
                    {group.rows.map((row) => (
                      <LevelRow
                        key={row.clinicItemId}
                        row={row}
                        clinicId={data.clinicId}
                        canEdit={data.canEdit}
                      />
                    ))}
                  </div>
                )}
              </CollapsibleSection>
            );
          })}
        </div>
      )}

      <AddSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        clinicId={data.clinicId}
        available={data.available}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* One row                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * An item on the carry list: its two numbers, its draft tag, and the two things
 * that can be done to it.
 *
 * Its own component because each row owns editing state — what is typed, what
 * is dirty, whether a removal is being confirmed — and hoisting ninety rows'
 * worth of that into the screen would re-render every row on every keystroke.
 *
 * At 390px the geometry is: name and pack size on their own lines, then the two
 * inputs side by side at half width each, then one line carrying the save and
 * the remove. Nothing scrolls sideways and nothing is under 24px.
 */
function LevelRow({
  row,
  clinicId,
  canEdit,
}: {
  row: Carried;
  clinicId: Id<"clinics">;
  canEdit: boolean;
}) {
  const upsert = useMutation(api.levels.upsert);
  const { busy, run } = useBusyAction();

  const [floor, setFloor] = useState(String(row.floorQty));
  const [target, setTarget] = useState(String(row.targetQty));
  const [confirming, setConfirming] = useState(false);
  const [saved, setSaved] = useState(false);

  /* The server is the source of truth for these two numbers, and it can change
     them underneath us — our own save, or the owner editing the same clinic in
     another tab. Resetting during render rather than in an effect is React's
     documented way to derive state from props: an effect would repaint once
     with the stale value first. */
  const fromServer = `${row.floorQty}/${row.targetQty}`;
  const seen = useRef(fromServer);
  if (seen.current !== fromServer) {
    seen.current = fromServer;
    setFloor(String(row.floorQty));
    setTarget(String(row.targetQty));
  }

  const basis = presentLevelsBasis(row.levelsBasis);
  const floorQty = Number(floor || 0);
  const targetQty = Number(target || 0);
  const dirty =
    floor !== String(row.floorQty) || target !== String(row.targetQty);
  /** The one refusal a person can cause, checked here so it never becomes a
   *  toast. Same sentence the server uses, so the two cannot drift apart in
   *  meaning. */
  const invalid = targetQty < floorQty;

  /* Integer par levels, and the decimal point is a trap: `"1.5"` stripped of
   * non-digits is `"15"`, a tenfold error the person never sees. Keep only what
   * precedes a separator, so `"1.5"` reads as `1`, not `15`. */
  const digits = (value: string) => value.split(/[.,]/)[0].replace(/\D/g, "");

  const save = () =>
    run(
      async () => {
        const result = await upsert({
          clinicId,
          itemId: row.itemId,
          floorQty,
          targetQty,
        });
        // Success only: written at the end of the action so a refused save
        // cannot claim to have gone through.
        setSaved(true);
        return result;
      },
      // No success toast. The Save button going away, the "Saved" line taking
      // its place and the Draft tag clearing are the confirmation, and ninety
      // rows of toasts is the wall this screen would otherwise become.
      { failed: `Could not save the levels for ${row.name}` },
    );

  const remove = () =>
    run(
      async () => {
        const result = await upsert({
          clinicId,
          itemId: row.itemId,
          active: false,
        });
        setConfirming(false);
        return result;
      },
      {
        failed: `Could not remove ${row.name}`,
        done: {
          happened: `${row.name} removed from the carry list`,
          next: "It stops appearing on the weekly count. Its history is kept.",
        },
      },
    );

  return (
    <div className="p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{row.name}</div>
          <div className="truncate text-xs text-muted-foreground">
            {row.packSize}
          </div>
        </div>
        {/* Null means draw nothing — a confirmed level wears no tag, the same
            way a manual request wears no origin badge. */}
        {basis ? (
          <span className="shrink-0">
            <Pill tone={basis.tone} label={basis.label} />
          </span>
        ) : null}
      </div>

      {!canEdit ? (
        <div className="num mt-1.5 text-sm text-muted-foreground">
          Floor <b className="font-semibold text-foreground">{row.floorQty}</b> ·
          target{" "}
          <b className="font-semibold text-foreground">{row.targetQty}</b>
        </div>
      ) : confirming ? (
        <div className="mt-2">
          {/* The consequence in words, next to the button that causes it —
              the Receive screen's convention. */}
          <p className="text-xs text-muted-foreground">
            Stops weekly counting of this item; history is kept.
          </p>
          <div className="mt-2 flex gap-2">
            <Button variant="outline" onClick={remove} disabled={busy}>
              {busy ? "Removing…" : "Remove"}
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Keep it
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-2 flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Label
                htmlFor={`${row.clinicItemId}-floor`}
                className="text-xs text-muted-foreground"
              >
                Floor
              </Label>
              {/* Digits only, `inputMode="numeric"` for the phone keypad, and
                  the registry's own `text-base` on a phone — 16px is what
                  stops iOS zooming the page on focus. The accessible name
                  names the item as well as the field: "Floor" alone, ninety
                  times, is not a name. */}
              <Input
                id={`${row.clinicItemId}-floor`}
                value={floor}
                onChange={(event) => {
                  setFloor(digits(event.target.value));
                  setSaved(false);
                }}
                inputMode="numeric"
                aria-label={`Floor for ${row.name}`}
                className="num mt-1 text-base"
              />
            </div>
            <div className="min-w-0 flex-1">
              <Label
                htmlFor={`${row.clinicItemId}-target`}
                className="text-xs text-muted-foreground"
              >
                Target
              </Label>
              <Input
                id={`${row.clinicItemId}-target`}
                value={target}
                onChange={(event) => {
                  setTarget(digits(event.target.value));
                  setSaved(false);
                }}
                inputMode="numeric"
                aria-label={`Target for ${row.name}`}
                aria-invalid={invalid}
                className="num mt-1 text-base"
              />
            </div>
          </div>

          {invalid ? (
            <p
              /* The field-level message, not a toast: the person is mid-edit
                 and one keystroke from valid. */
              role="alert"
              className="mt-1.5 text-xs font-medium text-red-700"
            >
              The target cannot be below the floor.
            </p>
          ) : floorQty === 0 ? (
            /* A floor of 0 can never be fallen below (`counts.submit` fires on
               `counted < floor`), so the item silently never tops up — the
               dangerous set is "floor 0", not only "0/0", which the old
               condition missed. Read off the live edit state, so it warns while
               you are setting it, not only after a save. */
            <p className="mt-1.5 text-xs text-amber-900">
              {targetQty === 0
                ? "No levels set yet — a count of this item can never raise a top-up."
                : "Floor 0 — a count can never fall below it, so this item never tops up. Set a floor to arm it."}
            </p>
          ) : null}

          <div className="mt-2 flex items-center justify-between gap-2">
            {dirty ? (
              <Button size="sm" onClick={save} disabled={busy || invalid}>
                {busy ? "Saving…" : "Save"}
              </Button>
            ) : saved ? (
              <span className="text-xs font-medium text-green-700">Saved</span>
            ) : (
              <span className="min-w-0 truncate text-xs text-muted-foreground">
                {row.levelsBasis === "draft"
                  ? "Draft estimate — not yet confirmed"
                  : `Set by ${row.updatedByName} · ${relativeDay(row.updatedAt)}`}
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirming(true)}
              className="shrink-0 text-muted-foreground"
            >
              Remove
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Adding                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Put another item on the carry list.
 *
 * A `cmdk` palette in a sheet, which is this app's answer to picking one of
 * ~300 items — the same primitive the request screen's two-tap flow is built
 * on, and the reason `<select>` is not on the table. Filtering is cmdk's own
 * here rather than a server search: the whole available list already arrived
 * with the screen, so a round trip per keystroke would buy nothing.
 *
 * **The add passes `active: true` explicitly**, and that is not decoration. An
 * item dropped earlier still has its row, `levels.upsert` merges onto what is
 * there, and a dropped row is `active: false` — so an add that omitted the flag
 * would look exactly like the save failing. Floor and target are deliberately
 * *not* sent: on a re-add that keeps the levels the clinic had before, and on a
 * genuinely new row the server's own default (0 and 0) applies and the row says
 * so in words.
 */
function AddSheet({
  open,
  onClose,
  clinicId,
  available,
}: {
  open: boolean;
  onClose: () => void;
  clinicId: Id<"clinics">;
  available: Array<{
    itemId: Id<"items">;
    name: string;
    subcategory: string;
    packSize: string;
  }>;
}) {
  const upsert = useMutation(api.levels.upsert);
  const { busy, run } = useBusyAction();

  const add = (item: (typeof available)[number]) =>
    run(
      async () => {
        const result = await upsert({
          clinicId,
          itemId: item.itemId,
          active: true,
        });
        onClose();
        return result;
      },
      {
        failed: `Could not add ${item.name}`,
        done: (result) => ({
          happened: `${item.name} added to the carry list`,
          next: result.targetQty === 0
            ? "It is on the weekly count now. Set its floor and target on the row."
            : "It is back on the weekly count, at the levels it had before.",
        }),
      },
    );

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Add an item to the carry list"
      description="It joins this clinic's weekly count. Set its floor and target on the row afterwards."
    >
      <Command
        className="pb-2"
        /* Substring, not cmdk's default fuzzy score. Measured here: typing
           "glove" scored "HS Digital sensor covers size 2" above the gloves,
           because command-score rewards scattered character hits. A picker
           that answers a typed word with an unrelated item is a picker nobody
           trusts, and the request screen sidesteps this a different way — it
           filters on the server (`shouldFilter={false}`). This list already
           arrived with the screen, so the fix is the predicate, not a query. */
        filter={(value, search) =>
          value.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0
        }
      >
        <CommandInput
          placeholder={`Search ${available.length} items…`}
          aria-label="Search items to add"
        />
        <CommandList className="max-h-[50vh] overscroll-contain">
          <CommandEmpty className="px-6 py-6 text-center text-sm text-muted-foreground">
            {available.length === 0
              ? "This clinic already carries every item in the catalogue."
              : "Nothing matching that. Check the spelling."}
          </CommandEmpty>
          {available.map((item) => (
            <CommandItem
              key={item.itemId}
              value={`${item.name} ${item.subcategory}`}
              disabled={busy}
              onSelect={() => add(item)}
              className="cursor-pointer gap-3 rounded-none border-b px-2 py-3 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm leading-tight font-medium">
                  {item.name}
                </div>
                <div className="mt-1 truncate text-xs text-muted-foreground">
                  {item.packSize}
                  {item.subcategory ? ` · ${item.subcategory}` : ""}
                </div>
              </div>
              {/* The whole row is the tap target, so this says "adds" rather
                  than being a second control inside it. */}
              <span
                aria-hidden
                className="grid size-8 shrink-0 place-content-center rounded-full border text-base font-semibold text-primary"
              >
                +
              </span>
            </CommandItem>
          ))}
        </CommandList>
      </Command>
    </Sheet>
  );
}
